import React, { useEffect, useMemo, useState } from "react";
import { Col, Row, Table } from "react-bootstrap";
import "./ReportClassTable.css";
import "./SencoStudentsResultsTable.css";
import {
  getSencoReportByClassId,
  getStudentClassDetail,
  getSencoReportBySchoolId,
  getSencoReportByStudentId,
  getNotes,
} from "../services/APIClient";

function ExpandedSENWatchlistView(props) {
  const {
    students,
    showAtRiskOnly,
    setShowAtRiskOnly,
    classid,
    studentsReadingScore,
    schoolid,
    onDataReady, // Callback to pass data to parent
    refreshKey,
    teachers,
  } = props;

  console.log(schoolid);

  const dummyHeaderArray = [
    "Class",
    "SET",
    "Classroom Support Plan",
    "Details Of SET Received",
    "Priority Concerns",
    "Additional Needs",
    "Support Plan",
    "EAL",
    "Place on Continuum",
  ];

  const [sencoReportDetails, setSencoReportDetails] = useState([]);
  const [classNames, setClassNames] = useState({}); // Cache for class names
  const [studentNotes, setStudentNotes] = useState({}); // Notes keyed by student ID

  const handleAtRiskFilterChange = (e) => {
    setShowAtRiskOnly(e.target.checked);
  };

  useEffect(() => {
    if (!students || students.length === 0) return;
    const fetchAll = async () => {
      const results = await Promise.all(
        students.map((student) =>
          getSencoReportByStudentId(student.id)
            .then((report) => report ? { ...report, studentid: student.id } : null)
            .catch(() => null)
        )
      );
      setSencoReportDetails(results.filter(Boolean));
    };
    fetchAll();
  }, [students, refreshKey]);

  useEffect(() => {
    if (!students || students.length === 0) return;
    students.forEach((student) => {
      getNotes("student", student.id, "SenWatchlist")
        .then((response) => {
          setStudentNotes((prev) => ({
            ...prev,
            [student.id]: response.data || [],
          }));
        })
        .catch(() => {
          setStudentNotes((prev) => ({ ...prev, [student.id]: [] }));
        });
    });
  }, [students]);

  const getTeacherName = (teacherid) => {
    if (!teachers) return "";
    const teacher = teachers.find((t) => t.id === teacherid);
    return teacher?.name || "";
  };

  const formatNoteDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString("en-IE", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "Europe/Dublin",
    });
  };

  const getBadgeLabel = (type) => {
    if (type === "initial") return "Initial";
    if (type === "removal") return "Removal";
    return "Note";
  };

  // Fetch and cache class name for a student
  const fetchClassName = (studentId) => {
    if (!classNames[studentId]) {
      getStudentClassDetail(studentId)
        .then((classDetails) => {
          if (classDetails?.className) {
            setClassNames((prev) => ({
              ...prev,
              [studentId]: classDetails.className,
            }));
          }
        })
        .catch((error) =>
          console.error(
            `Error fetching class details for student ${studentId}:`,
            error
          )
        );
    }
  };

  const formatList = (items) => (
    <>
      {items?.map((item, index) => (
        <div key={index} className="item-list">
          {item}
        </div>
      ))}
    </>
  );

  // Fetch class names as a proper side-effect, not inside render
  useEffect(() => {
    if (!students || students.length === 0) return;
    students.forEach((student) => fetchClassName(student.id));
  }, [students, sencoReportDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  const dataToDisplay = useMemo(
    () =>
      students.map((student) => {
        const matchingReport = sencoReportDetails.find(
          (report) => report.studentid === student.id
        ) || {
          classname: "",
          setname: "",
          classroomsupportplan: false,
          setreceived: [],
          priorityconcerns: [],
          additionalneeds: [],
          supportplan: [],
          eal: false,
          supportcontinuum: [],
        };

        return {
          ...student,
          ...matchingReport,
          id: student.id,
          classname: classNames[student.id] || "Loading...",
        };
      }),
    [students, sencoReportDetails, classNames]
  );

  useEffect(() => {
    if (!onDataReady) return;
    onDataReady({
      students: dataToDisplay,
      classid,
      showAtRiskOnly,
      studentsReadingScore,
      schoolid,
    });
  }, [dataToDisplay, classid, showAtRiskOnly, studentsReadingScore, schoolid, onDataReady]);

  return (
    <Row>
      <Col>
        <div style={{ overflowX: "auto", width: "100%" }}>
          <Table className="custom-table expanded-handover-table" style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th className="table-header-cell">Name</th>
                {dummyHeaderArray.map((header, index) => (
                  <th key={index} className="table-header-cell">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataToDisplay
                .filter(
                  (student) =>
                    !showAtRiskOnly ||
                    (student.readingScore !== undefined &&
                      student.readingScore !== null &&
                      student.readingScore <= 40)
                )
                .map((student) => (
                  <React.Fragment key={student.id}>
                    <tr className="table-row student-results-table-row">
                      <td>{student.name}</td>
                      <td>{student.classname}</td>
                      <td>{student.setname}</td>
                      <td>{student.classroomsupportplan ? "Yes" : "No"}</td>
                      <td>{formatList(student.setreceived)}</td>
                      <td>{formatList(student.priorityconcerns)}</td>
                      <td>{formatList(student.additionalneeds)}</td>
                      <td>{formatList(student.supportplan)}</td>
                      <td>{student.eal ? "Yes" : "No"}</td>
                      <td>{formatList(student.supportcontinuum)}</td>
                    </tr>
                    {(studentNotes[student.id] || []).length > 0 && (
                      <tr>
                        <td colSpan={10} style={{ background: "#f8f9fa", padding: "8px 16px 12px" }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 6 }}>
                            {student.name} - Watchlist Notes
                          </div>
                          {(studentNotes[student.id] || []).map((n) => (
                            <div
                              key={n.id}
                              style={{
                                marginBottom: 8,
                                padding: "8px 12px",
                                borderLeft: "3px solid #1a56db",
                                background: "white",
                                borderRadius: 4,
                              }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                                {/* <span style={{ fontSize: 11, fontWeight: "bold", background: "#1a56db", color: "white", padding: "2px 8px", borderRadius: 4 }}>
                                  {getBadgeLabel(n.type)}
                                </span> */}
                                <span style={{ fontSize: 11, color: "#666" }}>
                                  {getTeacherName(n.teacherid)}{getTeacherName(n.teacherid) ? " \u2022 " : ""}{formatNoteDate(n.date_created)}
                                </span>
                              </div>
                              <div style={{ fontSize: 13, color: "#333", whiteSpace: "pre-wrap" }}>{n.note_text}</div>
                            </div>
                          ))}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
            </tbody>
          </Table>
        </div>
      </Col>
    </Row>
  );
}

export default ExpandedSENWatchlistView;
